import { request, response } from "../../proto/bundle";
import { getConnectionPool } from "../../core/connectionPool";
import { Payment } from "./payment";
import { checkMigrate } from "../../init/sdkInit";
import { hashString } from "../../utils/hash";

export class User {
    constructor(
        private email: string,
        private totpUri: string,
        private operations: number[],
        private firstName?: string,
        private lastName?: string,
        private userType?: string,
        private organization?: string,
        private phone?: string,
        private address?: string,
        private plan?: number,
        private location?: string,
        private profilePicture?: Uint8Array,
        private planExpiry?: number | Long,
    ) { }

    public getEmail(): string {
        return this.email;
    }
    public setEmail(email: string): void {
        this.email = email;
    }
    public getTotpUri(): string {
        return this.totpUri;
    }
    public setTotpUri(totpUri: string): void {
        this.totpUri = totpUri;
    }
    public getFirstName(): string | undefined {
        return this.firstName;
    }
    public setFirstName(firstName: string): void {
        this.firstName = firstName;
    }
    public getLastName(): string | undefined {
        return this.lastName;
    }
    public setLastName(lastName: string): void {
        this.lastName = lastName;
    }
    public getName(): string {
        const name = (this.firstName ?? this.lastName ?? " ") + (this.lastName ?? "")
        return name;
    }
    public getUserType(): string | undefined {
        return this.userType;
    }
    public setUserType(userType: string): void {
        this.userType = userType;
    }
    public getOrganization(): string | undefined {
        return this.organization;
    }
    public setOrganization(organization: string): void {
        this.organization = organization;
    }
    public getPhone(): string | undefined {
        return this.phone;
    }
    public setPhone(phone: string): void {
        this.phone = phone;
    }
    public getAddress(): string | undefined {
        return this.address;
    }
    public setAddress(address: string): void {
        this.address = address;
    }
    public getPlan(): number | undefined {
        return this.plan;
    }
    public setPlan(plan: number): void {
        this.plan = plan;
    }
    public getLocation(): string | undefined {
        return this.location;
    }
    public setLocation(location: string): void {
        this.location = location;
    }
    public getProfilePicture(): Uint8Array | undefined {
        return this.profilePicture;
    }
    public setProfilePicture(profilePicture: Uint8Array | undefined): void {
        this.profilePicture = profilePicture;
    }
    public getPlanExpiry(): number | Long | undefined {
        return this.planExpiry;
    }
    public setPlanExpiry(planExpiry: number | Long): void {
        this.planExpiry = planExpiry;
    }


    public setOperations(operations: number[]) {
        this.operations = operations;
    }
    public getOperations(): number[] {
        return this.operations;
    }

    public async verify(emailOtp: string, totp: string, questions?: string[], answers?: string[]): Promise<boolean> {
        const queAndAns = (questions || []).map((question, index) =>
            request.QueAndAns.create({
                question,
                answer: answers[index] || ""
            })
        );
        let verifyUser = request.VerifyUser.create({
            email: this.email,
            totp, emailOtp,
            queAndAns
        });
        let req = request.Request.create({ id: null, reqCreateTime: Date.now(), verifyUser })
        const pool = await getConnectionPool();
        const VerifyUserResp = await pool.sendAndWait(req);
        checkMigrate(VerifyUserResp.verifyUser.status)

        if (VerifyUserResp?.verifyUser?.status != 0) {
            throw new Error(VerifyUserResp.verifyUser?.message || "Verify User failed");
        }

        return Promise.resolve(true);
    }

    public async startPayment(plan: number, isYearly: boolean, additionalStorage?: number, coupon?: string, gstToken?: string): Promise<Payment> {
        if (this.organization != "") {
            throw new Error("Organizational User not allowed to pay")
        }
        let initiatePayment = request.InitiatePayment.create({
            email: this.email,
            plan: plan,
            isYearly: isYearly,
            coupon: coupon,
            additionalStorage: additionalStorage,
            gstToken: gstToken,
        });
        let req = request.Request.create({ id: null, reqCreateTime: Date.now(), initiatePayment })
        const pool = await getConnectionPool();
        const InitiatePaymentResp = await pool.sendAndWait(req);
        checkMigrate(InitiatePaymentResp.initiatePayment.status)

        if (InitiatePaymentResp?.initiatePayment?.status != 0) {
            throw new Error(InitiatePaymentResp.initiatePayment?.message || "Initiate payment failed");
        }

        if ((InitiatePaymentResp.initiatePayment?.email ?? "") === "") {
            throw new Error("No email in response for Initiate payment(Initiate payment)");
        }

        return Promise.resolve(new Payment(
            InitiatePaymentResp.initiatePayment?.orderId ?? "",
            InitiatePaymentResp.initiatePayment?.razorpayKey ?? "",
            InitiatePaymentResp.initiatePayment?.amount ?? 0
        ))
    }

    public async finishPayment(payment: Payment): Promise<string> {
        let VerifyPayment = request.VerifyPayment.create({ orderId: payment.orderId });
        let req = request.Request.create({ id: null, reqCreateTime: Date.now(), VerifyPayment });
        const pool = await getConnectionPool();
        const VerifyPaymentResp = await pool.sendAndWait(req);
        checkMigrate(VerifyPaymentResp.verifyPayment.status)

        if (VerifyPaymentResp?.verifyPayment?.status != 0) {
            throw new Error(VerifyPaymentResp.verifyPayment?.message || "Verify Payment failed");
        }

        return Promise.resolve(VerifyPaymentResp.verifyPayment?.message || "Completed Payment");
    }

    public async remove(
        password: string
    ) {
        const hashed_password = await hashString(password)
        const removeUser = request.RemoveUser.create({ email: this.email, password: hashed_password, organization: this.organization })
        const req = request.Request.create({ id: null, reqCreateTime: Date.now(), removeUser })
        const pool = await getConnectionPool()
        const resp = await pool.sendAndWait(req);
        checkMigrate(resp.removeUser.status)

        if (resp?.removeUser?.status !== 0) {
            throw new Error(resp?.removeUser?.message ?? "Failed to remove user")
        }
    }

    async blockUser(email: string): Promise<void> {
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            blockUser: request.BlockUser.create({
                email: email,
            })
        });
        let resp = await pool.sendAndWait(req);
        if (!resp.blockUser || resp.blockUser.status !== response.Status.SUCCESS) {
            throw new Error(`${resp.blockUser?.message}`);
        }
    }
    async unblockUser(email: string): Promise<void> {
        const pool = await getConnectionPool();
        const req = request.Request.create({
            id: null,
            reqCreateTime: Date.now(),
            unblockUser: request.UnblockUser.create({
                email: email,
            })
        });
        let resp = await pool.sendAndWait(req);
        if (!resp.unblockUser || resp.unblockUser.status !== response.Status.SUCCESS) {
            throw new Error(`${resp.unblockUser?.message}`);
        }
    }
}
